// Gestori globali degli errori non gestiti. Usiamo `addEventListener` (non
// l'assegnazione di `window.onerror`) per non clobberare handler preesistenti.
// Ritornano una funzione di disinstallazione.

import type { Client } from '../../client'

/// Installa i listener `error` e `unhandledrejection`. No-op fuori dal browser.
export function installGlobalHandlers(client: Client): () => void {
  if (typeof window === 'undefined') return () => {}

  const onError = (event: Event): void => {
    const errorEvent = event as Partial<ErrorEvent> & Event
    // Errori di caricamento risorsa (img/script): nessun error/message → ignora.
    if (!errorEvent.error && !errorEvent.message) return

    if (errorEvent.error) {
      client.captureException(errorEvent.error, { handled: false })
      return
    }
    client.captureException(synthesizeError(errorEvent), { handled: false })
  }

  const onRejection = (event: Event): void => {
    const rejection = event as PromiseRejectionEvent
    const reason = rejection.reason ?? 'Unhandled promise rejection'
    client.captureException(reason, { handled: false })
  }

  window.addEventListener('error', onError, true)
  window.addEventListener('unhandledrejection', onRejection, true)

  return () => {
    window.removeEventListener('error', onError, true)
    window.removeEventListener('unhandledrejection', onRejection, true)
  }
}

// window.onerror non porta un oggetto Error: sintetizziamo name/message/stack da
// message/filename/lineno/colno così `parseStack` produce un frame coerente.
function synthesizeError(event: Partial<ErrorEvent>): {
  name: string
  message: string
  stack: string | undefined
} {
  const message = event.message ?? 'Unknown error'
  let stack: string | undefined
  if (event.filename) {
    const line = event.lineno ?? 0
    const col = event.colno ?? 0
    stack = `Error: ${message}\n    at ${event.filename}:${line}:${col}`
  }
  return { name: 'Error', message, stack }
}
